import { ApolloClient, ApolloLink, from } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import jwt, { JwtPayload } from 'jsonwebtoken'

import settings from 'shared/common/settings'
import cache from 'shared/services/apollo/cache'
import { createServerLinks } from 'shared/services/apollo/links/server'

import logger from '../logger'

type DecodedToken = JwtPayload | null

export function isTokenExpired(token: string): boolean {
  try {
    const decodedToken: DecodedToken = jwt.decode(token, { complete: true })
    if (!decodedToken || !decodedToken.payload || !decodedToken.payload.exp) {
      // Token is invalid or doesn't contain expiration info
      return true
    }
    const currentTimeInSeconds = Math.floor(Date.now() / 1000)
    return decodedToken.payload.exp < currentTimeInSeconds
  } catch (error) {
    logger.error(error, {
      feature: logger.Feature.Auth,
    })
    return true
  }
}

export function createApolloClient(
  accessToken?: string,
  version = '1.0',
): ApolloClient<object> {
  const authMiddleware = new ApolloLink((operation, forward) => {
    operation.setContext({
      headers: {
        authorization: accessToken ? `Bearer ${accessToken}` : '',
      },
    })

    return forward(operation)
  })

  return new ApolloClient({
    cache: cache,
    queryDeduplication: true,
    ssrMode: true,
    link: from([
      authMiddleware,
      setContext((_, ctx) => ({
        headers: { ...ctx.headers, 'x-graphql-version': version },
      })),
      createServerLinks(settings.getValue('API_URI')),
    ]),
  })
}

let client: ApolloClient<object>

export function makeIsomorphicClient(
  accessToken?: string,
  version = '1.0',
): ApolloClient<object> {
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
    const { createClient } = require('./client')

    if (!client) {
      client = createClient(version)
    }

    return client
  }

  return createApolloClient(accessToken, version)
}
